import { codigoEquipamento } from '../utils/codigoEquipamento';
import { OrcamentoIcon } from './OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from './LiquidUI';
import React, { useState } from 'react';
import api from '../services/api';
import './EquipmentDialogs.css';
import './DL50LoteModal.css';
import { descarregarBlob } from '../utils/abrirFicheiro';
import ChecklistDL50, { checklistConforme, checklistPara, tiposEpi } from './ChecklistDL50';

// DL50 em lote: a mesma verificação (técnico, data, check-list) para as fichas
// da lista de Equipamentos. Cada ficha fica com o seu relatório e número; vem
// tudo em «C» e uma ficha com alguma situação não conforme tira-se daqui e faz
// depois o DL50 individual. Cada ficha leva a check-list adaptada: num EPI
// (arnês, mosquetão, corda, fita) os itens de máquinas vão a «NA», e nas
// outras fichas os de EPI.

const hojeLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtData = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '');

const DL50LoteModal = ({ equipamentos, trabalhadores, obras, onFechar, onGravado }) => {
    const liquidFeedback = useLiquidFeedback();
    const [incluidos, setIncluidos] = useState(() => new Set(equipamentos.map(e => e.id)));
    const [form, setForm] = useState(() => ({
        tecnico_id: '',
        obra_id: '',
        data_verificacao: hojeLocal(),
        checklist: checklistConforme(),
        resultado_usavel: true,
        observacoes_finais: '',
    }));
    const [aGravar, setAGravar] = useState(false);

    const selecionados = equipamentos.filter(e => incluidos.has(e.id));
    const tecnico = trabalhadores.find(t => String(t.id) === String(form.tecnico_id));
    const podeGravar = !!tecnico && selecionados.length > 0 && !!form.data_verificacao && !aGravar;
    const temNC = Object.values(form.checklist).some(i => i?.status === 'NC');
    const agora = new Date();

    const alternar = (id) => setIncluidos(prev => {
        const novo = new Set(prev);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        return novo;
    });

    const gravar = async (gerarPdf) => {
        if (!podeGravar) return;
        const n = selecionados.length;
        if (!await liquidFeedback.confirm(`Gravar ${n} ${n === 1 ? 'relatório' : 'relatórios'} DL50 com data de ${fmtData(`${form.data_verificacao}T00:00:00`)} e técnico ${tecnico.nome}?`)) return;

        setAGravar(true);
        let res;
        try {
            res = await api.post('/dl50/lote', {
                equipamento_ids: selecionados.map(e => e.id),
                tecnico_id: tecnico.id,
                obra_id: form.obra_id ? Number(form.obra_id) : null,
                data_verificacao: form.data_verificacao,
                tipo_verificacao: 'Montagem inicial',
                checklist: form.checklist,
                checklists: Object.fromEntries(selecionados.map(e => [e.id, checklistPara(e, form.checklist)])),
                resultado_usavel: form.resultado_usavel,
                observacoes_finais: form.observacoes_finais,
            });
        } catch (error) {
            console.error('Erro ao gravar o DL50 em lote:', error);
            setAGravar(false);
            liquidFeedback.notify(error.response?.data?.error || 'Erro ao gravar o DL50 em lote.');
            return;
        }

        const { criados, ignorados, relatorios } = res.data;
        let pdfFalhou = false;
        if (gerarPdf && relatorios.length) {
            try {
                const pdf = await api.post('/dl50/relatorios', { ids: relatorios.map(r => r.id) }, { responseType: 'blob' });
                descarregarBlob(pdf.data, `DL50_lote_${form.data_verificacao}.pdf`);
            } catch (error) {
                console.error('Erro ao gerar o PDF do DL50 em lote:', error);
                pdfFalhou = true;
            }
        }
        onGravado();

        const nums = relatorios.map(r => r.relatorio_num);
        liquidFeedback.notify([
            `${criados} ${criados === 1 ? 'relatório DL50 gravado' : 'relatórios DL50 gravados'}${nums.length ? ` (${nums.length > 1 ? `${nums[0]} a ${nums[nums.length - 1]}` : nums[0]})` : ''}.`,
            ignorados ? `${ignorados} ${ignorados === 1 ? 'ficha já não existia e ficou' : 'fichas já não existiam e ficaram'} de fora.` : '',
            pdfFalhou ? 'Não foi possível gerar o PDF — cada relatório pode ser aberto em Relatórios.' : '',
        ].filter(Boolean).join('\n'), { icon: pdfFalhou ? 'warning' : 'check' });
    };

    const botaoInativo = podeGravar ? undefined : { opacity: 0.5, cursor: 'not-allowed' };
    const motivo = !tecnico ? 'Escolha o técnico responsável.'
        : selecionados.length === 0 ? 'Não há fichas selecionadas.'
            : !form.data_verificacao ? 'Indique a data da verificação.' : '';

    return (
        <LiquidDialog onClose={aGravar ? undefined : onFechar} className="modal-overlay" onClick={aGravar ? undefined : onFechar}>
            <div className="equipment-dialog dl50-bulk-dialog" onClick={e => e.stopPropagation()} style={{ maxWidth: '1000px' }}
                role="dialog" aria-label="DL50 em lote">
                <div className="equipment-dialog-header">
                    <h3><OrcamentoIcon name="dl50" fallback="📋" /> DL50 em lote — {equipamentos.length} {equipamentos.length === 1 ? 'ficha' : 'fichas'}</h3>
                    <button className="equipment-dialog-close" onClick={onFechar} disabled={aGravar} aria-label="Fechar"><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>
                <form onSubmit={(e) => e.preventDefault()} className="dl50-bulk-form">
                    <div className="equipment-dialog-body dl50-bulk-body">
                        <div style={{ background: '#ebf8ff', border: '1px solid #bee3f8', color: '#2c5282', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: '0.88rem', lineHeight: 1.5 }}>
                            A mesma verificação — técnico, data e check-list — fica gravada em cada ficha selecionada, cada uma com o seu relatório e número.
                            Vem tudo marcado <b>«C» (Conforme)</b>. Se alguma ficha tiver uma situação não conforme, desmarque-a aqui e faça depois o DL50 individual (<OrcamentoIcon name="document" fallback="📋" /> na linha).
                            Nas fichas de <b>EPI</b> (arnês, mosquetões, cordas, fitas) contam os itens de EPI desse tipo e os de máquinas (1 a 20) ficam NA; nas outras fichas, os de EPI ficam NA.
                        </div>

                        <div data-liquid-surface="true" style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ margin: '0 0 15px 0', fontSize: '1rem', color: '#2b6cb0' }}><OrcamentoIcon name="document" fallback="📋" /> Identificação da Verificação</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="dl50-lote-tecnico">Técnico Resp. *</label>
                                    <select id="dl50-lote-tecnico" value={form.tecnico_id} onChange={(e) => setForm(f => ({ ...f, tecnico_id: e.target.value }))}>
                                        <option value="">Selecione...</option>
                                        {trabalhadores.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="dl50-lote-obra">Obra / Local</label>
                                    <select id="dl50-lote-obra" value={form.obra_id} onChange={(e) => setForm(f => ({ ...f, obra_id: e.target.value }))}>
                                        <option value="">Nenhuma / Outra</option>
                                        {obras.map(o => <option key={o.id} value={o.id}>{o.posicao}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="dl50-lote-data">Data Verificação</label>
                                    <input id="dl50-lote-data" type="date" value={form.data_verificacao} onChange={(e) => setForm(f => ({ ...f, data_verificacao: e.target.value }))} />
                                </div>
                            </div>
                        </div>

                        <div className="dl50-bulk-list" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
                            <div style={{ background: '#f8fafc', padding: '10px 15px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#4a5568', flex: 1 }}>
                                    <OrcamentoIcon name="forms" fallback="🗂️" /> Fichas incluídas: {selecionados.length} de {equipamentos.length}
                                </h3>
                                <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                                    onClick={() => setIncluidos(new Set(equipamentos.map(e => e.id)))}>Todas</button>
                                <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                                    onClick={() => setIncluidos(new Set())}>Nenhuma</button>
                            </div>
                            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                                {equipamentos.map(eq => {
                                    const incluido = incluidos.has(eq.id);
                                    const ident = eq.n_serie || eq.matricula;
                                    const vencido = eq.dl50_proxima_verificacao && new Date(eq.dl50_proxima_verificacao) < agora;
                                    const epi = tiposEpi(eq);
                                    return (
                                        <label key={eq.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 15px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: incluido ? '#fff' : '#f8fafc', opacity: incluido ? 1 : 0.55 }}>
                                            <input type="checkbox" checked={incluido} onChange={() => alternar(eq.id)} aria-label={`Incluir ${codigoEquipamento(eq.id)} · ${eq.nome}`} />
                                            <span style={{ flex: 1, minWidth: 0, fontSize: '0.88rem' }}>
                                                <b>{codigoEquipamento(eq.id)} · {eq.nome}</b>
                                                {epi.length > 0 && (
                                                    <span title="Equipamento de proteção contra quedas: contam os itens de EPI"
                                                        style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, background: '#fffaf0', color: '#c05621', whiteSpace: 'nowrap' }}>
                                                        <OrcamentoIcon name="shield" fallback="🦺" /> EPI · {epi.map(t => t.label).join(', ')}
                                                    </span>
                                                )}
                                                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                                                    {[[eq.marca, eq.modelo].filter(Boolean).join(' '), ident && `Nº ${ident}`].filter(Boolean).map(t => ` · ${t}`).join('')}
                                                </span>
                                            </span>
                                            <span style={{ fontSize: '0.78rem', whiteSpace: 'nowrap', color: vencido ? '#c53030' : '#64748b' }}>
                                                {eq.dl50_proxima_verificacao ? `DL50 até ${fmtData(eq.dl50_proxima_verificacao)}` : 'Sem DL50'}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <ChecklistDL50 checklist={form.checklist} onChange={(checklist) => setForm(f => ({ ...f, checklist }))} />

                        <div className="form-section" style={{ marginTop: '20px', borderBottom: 'none' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input type="checkbox" checked={form.resultado_usavel} onChange={(e) => setForm(f => ({ ...f, resultado_usavel: e.target.checked }))} />
                                <b>EQUIPAMENTO APTO PARA UTILIZAÇÃO?</b>
                            </label>
                            <textarea placeholder="Observações finais..." rows="2" style={{ marginTop: '10px', width: '100%' }}
                                value={form.observacoes_finais} onChange={(e) => setForm(f => ({ ...f, observacoes_finais: e.target.value }))}></textarea>
                            {(temNC || !form.resultado_usavel) && (
                                <div role="alert" style={{ marginTop: 10, background: '#fffaf0', border: '1px solid #fbd38d', color: '#9c4221', borderRadius: 8, padding: '8px 12px', fontSize: '0.85rem' }}>
                                    <OrcamentoIcon name="warning" fallback="⚠️" /> {temNC ? 'Há itens marcados «NC»' : 'O equipamento está marcado como não apto'} — fica assim em todas as {selecionados.length} fichas selecionadas.
                                    Uma situação não conforme de uma só ficha faz-se no DL50 individual.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="equipment-dialog-footer dl50-bulk-footer">
                        {motivo && <span style={{ marginRight: 'auto', color: '#c05621', fontSize: '0.88rem' }}>{motivo}</span>}
                        <button type="button" className="btn-secondary" onClick={onFechar} disabled={aGravar}>Cancelar</button>
                        <button type="button" className="btn-secondary" onClick={() => gravar(false)} disabled={!podeGravar} style={botaoInativo}>
                            <OrcamentoIcon name="save" fallback="✓" /> {aGravar ? 'A gravar…' : `Gravar (${selecionados.length})`}
                        </button>
                        <button type="button" className="btn-primary" data-liquid-primary="true" onClick={() => gravar(true)} disabled={!podeGravar}
                            style={{ background: '#48bb78', ...botaoInativo }}>
                            <OrcamentoIcon name="document" fallback="📄" /> Gravar e Gerar PDF ({selecionados.length})
                        </button>
                    </div>
                </form>
            </div>
        </LiquidDialog>
    );
};

export default DL50LoteModal;
